import { ConditionsManager } from '@/shared/components/conditions/models/conditionsManager';
import { ConditionPickerOption, ConditionDataType } from '@/shared/components/conditions/models/conditions';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { store } from '@/store';
import { ChannelIdentityViewModel } from '@/messaging/models/channelIdentityViewModel';

export enum TriggerConditionAttributeType {
	MessageText = 'message.text',
	MessageIntent = 'message.intentId',
	EventSource = 'event.source',
	CurrentPageUrl = 'appUser.currentUrl',
	ChannelIdentity = 'event.channelIdentity',
	TimeOnPage = 'timeOnPage'
}

export class TriggerConditionsManager extends ConditionsManager {
	private conditionsToDisplay: ConditionPickerOption[] = [];
	private allConditions: ConditionPickerOption[] = [
		{
			id: TriggerConditionAttributeType.MessageText,
			title: 'Message Text',
			type: ConditionDataType.Text,
			icon: 'MessageIcon'
		},
		{
			id: TriggerConditionAttributeType.MessageIntent,
			title: 'Message Intent',
			type: ConditionDataType.Intent,
			icon: 'MessageIcon'
		},
		{
			id: TriggerConditionAttributeType.EventSource,
			title: 'Channel',
			type: ConditionDataType.List,
			icon: 'ListIcon'
		},
		{
			id: TriggerConditionAttributeType.ChannelIdentity,
			title: 'Channel Identity',
			type: ConditionDataType.List,
			icon: 'ListIcon'
		},
		{
			id: TriggerConditionAttributeType.CurrentPageUrl,
			title: 'Current Page URL',
			type: ConditionDataType.Text,
			icon: 'PageIcon'
		},
		{
			id: TriggerConditionAttributeType.TimeOnPage,
			title: 'Time on Page',
			type: ConditionDataType.Duration,
			icon: 'DurationIcon'
		}
	];

	private channelOptions = [
		{
			id: 'sms-bt',
			title: 'BT Smart Messaging Tailored'
		},
		{
			id: 'google-bm',
			title: 'Google Business Messages'
		},
		{
			id: 'sms-govuknotify',
			title: 'GOV.UK Notify SMS'
		},
		{
			id: 'instagram',
			title: 'Instagram'
		},
		{
			id: 'messenger',
			title: 'Messenger'
		},
		{
			id: 'twitter-dm',
			title: 'Twitter'
		},
		{
			id: 'web-assistant',
			title: 'Web Assistant'
		}
	];

	constructor() {
		super([]);
		this.conditionsToDisplay = this.allConditions;
	}

	public excludeConditions(excludedConditions: string[]) {
		if (excludedConditions.length > 0) {
			this.conditionsToDisplay = this.allConditions.filter(c => !excludedConditions.includes(c.id));
		} else {
			this.conditionsToDisplay = this.allConditions;
		}
	}

	public getConditionOptions(condition: ConditionViewModel) {
		if (condition.attribute === TriggerConditionAttributeType.EventSource) {
			return this.channelOptions;
		} else if (condition.attribute === TriggerConditionAttributeType.ChannelIdentity) {
			const channelIds = store.getters.channelIdentities;
			return channelIds.map((c: ChannelIdentityViewModel) => ( {id: c.id, title: c.name}));
		}
		return '';
	}

	public getListConditionValueTitle(condition: ConditionViewModel) {
		if (condition.attribute === TriggerConditionAttributeType.EventSource) {
			const channel = this.channelOptions.filter(c => c.id === condition.value);
			if (channel && channel.length === 1) {
				return channel[0].title;
			}
		}

		if (condition.attribute === TriggerConditionAttributeType.ChannelIdentity) {
			const channelIdentity = store.getters.channelIdentities.filter((c: ChannelIdentityViewModel) => c.id === condition.value);
			if (channelIdentity && channelIdentity.length === 1) {
				return channelIdentity[0].name;
			}
		}
		return condition.value;
	}

	public getIntentConditionValueTitle(condition: ConditionViewModel) {
		const intentId = condition.value;
		// Getter relies on intents already having been loaded and in cache
		return store.getters.getIntentName(intentId) || intentId;
	}

	public isConditionValid(condition: ConditionViewModel): boolean {
		if (condition.attribute.startsWith('event.channelIdentity') && condition.value) {
			if (store.getters.channelIdentities.filter((c: ChannelIdentityViewModel) => c.id === condition.value).length === 0) {
				return false;
			}
		}
		if (condition.type === 'intent' && condition.value) {
			return !!store.getters.getIntentName(condition.value);
		}
		return !condition.invalid;
	}

	public get visibleConditions(): ConditionPickerOption[] {
		return this.conditionsToDisplay;
	}

	public get conditions(): ConditionPickerOption[] {
		return this.allConditions;
	}
}
